import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import type { DraggableData, DraggableEvent } from "react-draggable";
import { Button, Card, Image } from "antd";
import {
  CloseOutlined,
  HolderOutlined,
  MinusOutlined,
  DownOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import type { VictimDTO } from "@shared/contracts";
import { formatDateYMD } from "../../../utils/date";

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const PanelCard = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 500;
  width: 300px;
  height: fit-content;
  background: #393e46;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: veterantypewriter, monospace;
  color: #eeeeee;
  padding-bottom: ${({ $collapsed }) => ($collapsed ? 0 : 10)}px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: #222831;
  cursor: move;
  user-select: none;
  font-size: 12px;
  color: #aaaaaa;
  letter-spacing: 0.5px;
  gap: 6px;
`;

const HandleLabel = styled.span`
  flex: 1;
  text-transform: uppercase;
  font-size: 11px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

const CloseBtn = styled(Button)`
  height: 22px;
  width: 22px !important;
  min-width: 22px;
  padding: 0;
  border-radius: 4px;
  font-size: 12px;
  background: transparent !important;
  color: #aaaaaa !important;
  border: none !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:hover {
    color: #eeeeee !important;
    background: transparent !important;
  }
`;

const Body = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;
  font-size: 12px;
`;

const PictureWrapper = styled(Card)<{ $height: number }>`
  width: 120px;
  height: ${({ $height }) => $height}px;
  overflow: hidden;
  border-radius: 0;
  border-bottom: 16px solid white;
  align-self: center;
  margin-bottom: 4px;

  .ant-card-body {
    padding: 6px;
  }

  .ant-image,
  .ant-image-img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    object-position: center top;
    filter: grayscale(100%);
  }
`;

const StyledImage = styled(Image)`
  display: block;
  width: 100%;
  height: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const FieldLabel = styled.span`
  color: #aaaaaa;
  flex: 0 0 auto;
  white-space: nowrap;
`;

const FieldValue = styled.span`
  text-align: end;
  flex: 1 1 auto;
  word-break: break-word;
`;

const MoreInfoButton = styled.a`
  align-self: center;
  color: #aaaaaa;
  font-size: 11px;
  text-decoration: none;
  word-break: break-all;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: #eeeeee;
    text-decoration: underline;
  }
`;

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  victim: VictimDTO;
  color: string;
  onClose: () => void;
}

const MAX_IMAGE_HEIGHT = 240;

export function VictimDetailPanel({ victim, color, onClose }: Props) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [imageHeight, setImageHeight] = useState(MAX_IMAGE_HEIGHT);
  const [pos, setPos] = useState(() => ({
    x: Math.max(0, Math.round(window.innerWidth / 2 - 130)),
    y: Math.max(0, Math.round(window.innerHeight / 2 - 200)),
  }));
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });

  const computeBounds = () => {
    if (nodeRef.current) {
      setBounds({
        left: 0,
        top: 0,
        right: window.innerWidth - nodeRef.current.offsetWidth,
        bottom: window.innerHeight - nodeRef.current.offsetHeight,
      });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const node = nodeRef.current;
      if (!node) return;
      const maxX = window.innerWidth - node.offsetWidth;
      const maxY = window.innerHeight - node.offsetHeight;
      setBounds({ left: 0, top: 0, right: maxX, bottom: maxY });
      setPos((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDrag = (_: DraggableEvent, data: DraggableData) => {
    setPos({ x: data.x, y: data.y });
  };

  const handleDragStop = (_: DraggableEvent, data: DraggableData) => {
    setPos({ x: data.x, y: data.y });
  };

  const rows: Array<[string, string | number | null]> = [
    ["Name", victim.name ?? null],
    ["Status", victim.status ?? null],
    ["Gender", victim.gender ?? null],
    ["Age", victim.age ?? null],
    ["Race", victim.race ?? null],
    ["Hair color", victim.hairColor ?? null],
    ["Height", victim.height ?? null],
    ["Weight", victim.weight ?? null],
    [
      "Date of birth",
      victim.dateOfBirth ? formatDateYMD(victim.dateOfBirth) : null,
    ],
    ["Place of birth", victim.placeOfBirth ?? null],
    [
      "Date of crime",
      victim.dateOfCrime ? formatDateYMD(victim.dateOfCrime) : null,
    ],
    ["Address of crime", victim.addressOfCrime ?? null],
    ["Cause of death", victim.causeOfDeath ?? null],
    ["Country", victim.country ?? null],
  ];

  const visibleRows = rows.filter(([, v]) => v != null && v !== "");

  return (
    <Draggable
      nodeRef={nodeRef}
      cancel="button, a, input, textarea, .ant-btn, .ant-btn-icon, .anticon"
      position={pos}
      bounds={bounds}
      onStart={computeBounds}
      onDrag={handleDrag}
      onStop={handleDragStop}
    >
      <PanelCard ref={nodeRef} $collapsed={collapsed}>
        <DragHandle className="drag-handle">
          <HolderOutlined style={{ fontSize: 14, opacity: 0.5 }} />
          <ColorDot $color={color} />
          <HandleLabel>{victim.name || "Victim"}</HandleLabel>
          <CloseBtn
            icon={collapsed ? <DownOutlined /> : <MinusOutlined />}
            aria-label={collapsed ? "Expand" : "Collapse"}
            onClick={() => setCollapsed((c) => !c)}
            onMouseDown={(e) => e.stopPropagation()}
          />
          <CloseBtn
            icon={<CloseOutlined />}
            aria-label="Close"
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </DragHandle>

        <div style={{ display: collapsed ? "none" : "contents" }}>
          <Body>
            {victim.pictureLink && (
              <PictureWrapper $height={imageHeight}>
                <StyledImage
                  src={victim.pictureLink}
                  alt={victim.name ?? "Victim"}
                  preview={false}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    const scaled = Math.round(
                      (img.naturalHeight / img.naturalWidth) * 200,
                    );
                    setImageHeight(Math.min(scaled, MAX_IMAGE_HEIGHT));
                  }}
                />
              </PictureWrapper>
            )}

            {visibleRows.map(([label, value]) => (
              <FieldRow key={label}>
                <FieldLabel>{label}</FieldLabel>
                <FieldValue>{value}</FieldValue>
              </FieldRow>
            ))}

            {victim.wikipediaLink && (
              <MoreInfoButton
                href={victim.wikipediaLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                More Information
              </MoreInfoButton>
            )}
          </Body>
        </div>
      </PanelCard>
    </Draggable>
  );
}
